"use client";

import { useId } from "react";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/**
 * A block on a ramp with its forces drawn as arrows from the block's centre: weight
 * straight down, the normal force perpendicular to the slope, friction along it, and an
 * optional push. Arrow lengths share one scale (weight is a fixed length). All text
 * arrives through props; colours are attributes so the picture survives a PNG export.
 */
export interface InclineDiagramLabels {
  title: string;
  weight: string;
  normal: string;
  friction: string;
  applied: string;
  angle: string;
}

export interface InclineDiagramForces {
  weight: number;
  normal: number;
  friction: number;
  frictionDirection: "up" | "down" | "none";
  /** Positive pushes up the slope. */
  applied: number;
}

const WIDTH = 380;
const HEIGHT = 300;
const ORIGIN = { x: 36, y: 276 };
const MAX_SLOPE = 280;
const MAX_RISE = 150;
const BLOCK = { along: 44, across: 32 };
const WEIGHT_LENGTH = 92;
const MIN_ARROW = 26;
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;
const FONT = "system-ui, -apple-system, Segoe UI, sans-serif";

interface Arrow {
  key: string;
  dx: number;
  dy: number;
  label: string;
  color: string;
}

function arrowLength(force: number, weight: number): number {
  return force > 0 ? Math.max(MIN_ARROW, (WEIGHT_LENGTH * force) / weight) : 0;
}

export function InclineDiagram({
  angle,
  forces,
  labels,
}: {
  angle: number;
  forces: InclineDiagramForces;
  labels: InclineDiagramLabels;
}) {
  const uid = useId().replace(UNSAFE_ID, "");
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  const slope = sin > 0.01 ? Math.min(MAX_SLOPE, MAX_RISE / sin) : MAX_SLOPE;
  const base = { x: ORIGIN.x + slope * cos, y: ORIGIN.y };
  const top = { x: base.x, y: ORIGIN.y - slope * sin };
  const along = { x: cos, y: -sin };
  const out = { x: -sin, y: -cos };
  const centre = {
    x: ORIGIN.x + 0.52 * slope * cos + out.x * (BLOCK.across / 2),
    y: ORIGIN.y - 0.52 * slope * sin + out.y * (BLOCK.across / 2),
  };
  const w = forces.weight;
  const wLen = arrowLength(w, w);
  const arrows: Arrow[] = [
    {
      key: "weight",
      dx: 0,
      dy: wLen,
      label: labels.weight,
      color: CHART_COLORS[0],
    },
  ];
  const nLen = arrowLength(forces.normal, w);
  if (nLen > 0) {
    arrows.push({
      key: "normal",
      dx: out.x * nLen,
      dy: out.y * nLen,
      label: labels.normal,
      color: CHART_COLORS[2],
    });
  }
  const fLen = arrowLength(forces.friction, w);
  if (fLen > 0 && forces.frictionDirection !== "none") {
    const sign = forces.frictionDirection === "up" ? 1 : -1;
    arrows.push({
      key: "friction",
      dx: along.x * fLen * sign,
      dy: along.y * fLen * sign,
      label: labels.friction,
      color: CHART_COLORS[3],
    });
  }
  const aLen = arrowLength(Math.abs(forces.applied), w);
  if (aLen > 0) {
    const sign = forces.applied > 0 ? 1 : -1;
    arrows.push({
      key: "applied",
      dx: along.x * aLen * sign,
      dy: along.y * aLen * sign,
      label: labels.applied,
      color: CHART_COLORS[1],
    });
  }
  const marker = (key: string) => `incline-arrow-${uid}-${key}`;
  const arcRadius = 38;
  const arcEnd = {
    x: ORIGIN.x + arcRadius * cos,
    y: ORIGIN.y - arcRadius * sin,
  };
  return (
    <svg
      aria-label={labels.title}
      className="h-auto w-full"
      fontFamily={FONT}
      role="img"
      style={{ maxWidth: "26rem" }}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{labels.title}</title>
      <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
      <defs>
        {arrows.map((arrow) => (
          <marker
            id={marker(arrow.key)}
            key={arrow.key}
            markerHeight="7"
            markerWidth="7"
            orient="auto"
            refX="6"
            refY="3.5"
          >
            <path d="M0 0 L7 3.5 L0 7 Z" fill={arrow.color} />
          </marker>
        ))}
      </defs>
      {sin > 0.005 ? (
        <polygon
          fill={CHART_INK.grid}
          points={`${ORIGIN.x},${ORIGIN.y} ${base.x},${base.y} ${top.x},${top.y}`}
          stroke={CHART_INK.axis}
          strokeWidth={1.5}
        />
      ) : (
        <line
          stroke={CHART_INK.axis}
          strokeWidth={2}
          x1={ORIGIN.x}
          x2={base.x}
          y1={ORIGIN.y}
          y2={ORIGIN.y}
        />
      )}
      {sin > 0.005 ? (
        <>
          <path
            d={`M${ORIGIN.x + arcRadius} ${ORIGIN.y} A${arcRadius} ${arcRadius} 0 0 0 ${arcEnd.x} ${arcEnd.y}`}
            fill="none"
            stroke={CHART_INK.text}
            strokeWidth={1.25}
          />
          <text
            dominantBaseline="middle"
            fill={CHART_INK.text}
            fontSize={12}
            x={ORIGIN.x + (arcRadius + 22) * Math.cos(angle / 2) - 6}
            y={ORIGIN.y - (arcRadius + 12) * Math.sin(angle / 2)}
          >
            {labels.angle}
          </text>
        </>
      ) : null}
      <g
        transform={`translate(${centre.x} ${centre.y}) rotate(${(-angle * 180) / Math.PI})`}
      >
        <rect
          fill={CHART_INK.background}
          height={BLOCK.across}
          stroke={CHART_INK.text}
          strokeWidth={2}
          width={BLOCK.along}
          x={-BLOCK.along / 2}
          y={-BLOCK.across / 2}
        />
      </g>
      {arrows.map((arrow) => (
        <g key={arrow.key}>
          <line
            markerEnd={`url(#${marker(arrow.key)})`}
            stroke={arrow.color}
            strokeWidth={2.5}
            x1={centre.x}
            x2={centre.x + arrow.dx}
            y1={centre.y}
            y2={centre.y + arrow.dy}
          />
          <text
            dominantBaseline="middle"
            fill={CHART_INK.text}
            fontSize={13}
            fontWeight={600}
            textAnchor="middle"
            x={
              centre.x +
              arrow.dx +
              (arrow.dx >= 0 ? 12 : -12) * Math.abs(Math.sign(arrow.dx))
            }
            y={centre.y + arrow.dy + (arrow.dy >= 0 ? 12 : -10)}
          >
            {arrow.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
